import { Media } from '@capacitor-community/media';
import { isNativePlatform, getPlatform } from './platform';

const ALBUM_NAME = 'DecorCanvas';

/**
 * Result type for gallery save operations
 */
export type GallerySaveResult = {
  success: boolean;
  error?: string;
};

/**
 * Convert a URL to base64 data URI by fetching and reading
 */
const fetchImageAsDataUri = async (imageUrl: string): Promise<string> => {
  const response = await fetch(imageUrl);
  if (!response.ok) throw new Error('Failed to fetch image');

  const blob = await response.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      resolve(result); // Returns full data URI: data:image/...;base64,...
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * The @capacitor-community/media plugin v8 handles permissions automatically
 * when calling savePhoto(). If permissions haven't been granted, the native
 * code will prompt the user. We just need to handle the error if denied.
 * 
 * On Android 13+, we need READ_MEDIA_IMAGES permission (added to AndroidManifest.xml).
 * The plugin automatically requests this when saving.
 */

/**
 * Generate a unique filename with timestamp to prevent overwriting
 */
const generateUniqueFileName = (prefix: string): string => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8);
  return `${prefix}-${timestamp}-${random}`;
};

/**
 * Get or create album and return its IDENTIFIER using getAlbumsPath()
 * Critical: On Android, must use getAlbumsPath() to find the correct album
 */
const getAlbumIdentifier = async (): Promise<string | null> => {
  try {
    // Step 1: Try to create album (ignore error if already exists)
    try {
      await Media.createAlbum({ name: ALBUM_NAME });
      console.log('Album created or already exists:', ALBUM_NAME);
    } catch (e) {
      // Album may already exist, this is fine
      console.log('Album creation note:', e);
    }

    // Step 2: Get the base albums path (Android-specific requirement)
    const { path: albumsPath } = await Media.getAlbumsPath();
    console.log('Albums base path:', albumsPath);

    // Step 3: Get all albums and find our album using path validation
    const { albums } = await Media.getAlbums();
    
    console.log('Available albums:', albums.map(a => ({ name: a.name, id: a.identifier })));
    
    // CRITICAL: On Android, validate album identifier starts with albumsPath
    const decorAlbum = albums.find(
      a => a.name === ALBUM_NAME && a.identifier.startsWith(albumsPath)
    );
    
    if (decorAlbum?.identifier) {
      console.log('DecorCanvas album identifier:', decorAlbum.identifier);
      return decorAlbum.identifier;
    }
    
    // Fallback: just find by name if path validation fails
    const fallbackAlbum = albums.find(a => a.name === ALBUM_NAME);
    if (fallbackAlbum?.identifier) {
      console.log('Using fallback album identifier:', fallbackAlbum.identifier);
      return fallbackAlbum.identifier;
    }
    
    console.error('Album not found in getAlbums() result');
    return null;
  } catch (error) {
    console.error('Failed to get album identifier:', error);
    return null;
  }
};

/**
 * Save image to Android Gallery using Media plugin (MediaStore API)
 * This ensures images are visible in Gallery/Photos app immediately
 */
export const saveImageToGallery = async (
  imageUrlOrBase64: string,
  fileName: string
): Promise<GallerySaveResult> => {
  if (!isNativePlatform() || getPlatform() !== 'android') {
    return { success: false, error: 'Not Android' };
  }

  try {
    // Note: The Media plugin handles permission prompts automatically when saving.
    // If permissions are denied, it will throw an error which we catch below.

    // Step 2: Get album identifier using getAlbumsPath() validation
    const albumIdentifier = await getAlbumIdentifier();
    if (!albumIdentifier) {
      console.error('Failed to get album identifier - album may not have been created');
      return { success: false, error: 'Album identifier not found' };
    }

    // Step 3: Prepare image path - Media plugin accepts data URIs and URLs directly
    let imagePath: string;
    if (imageUrlOrBase64.startsWith('data:')) {
      // Already a data URI, use directly
      imagePath = imageUrlOrBase64;
      console.log('Using data URI directly (length:', imagePath.length, ')');
    } else if (imageUrlOrBase64.startsWith('http')) {
      // URL - convert to data URI for reliability
      console.log('Converting URL to data URI:', imageUrlOrBase64.substring(0, 50) + '...');
      imagePath = await fetchImageAsDataUri(imageUrlOrBase64);
      console.log('Data URI created (length:', imagePath.length, ')');
    } else {
      // Assume it's already a file path
      imagePath = imageUrlOrBase64;
      console.log('Using file path:', imagePath);
    }

    // Step 4: Generate unique filename to prevent overwriting
    const baseFileName = fileName.replace(/\.[^.]+$/, '').replace(/-\d+-[a-z0-9]+$/, '');
    const uniqueFileName = generateUniqueFileName(baseFileName);
    
    console.log('Calling Media.savePhoto with:', {
      pathType: imagePath.startsWith('data:') ? 'dataUri' : 'filePath',
      albumIdentifier: albumIdentifier,
      fileName: uniqueFileName,
    });

    // Step 5: Save to gallery using MediaStore API
    const result = await Media.savePhoto({
      path: imagePath,
      albumIdentifier: albumIdentifier,
      fileName: uniqueFileName,
    });

    console.log('SavePhoto result:', result);
    console.log('✅ Image saved to Gallery in album:', ALBUM_NAME, 'as:', uniqueFileName);

    return { success: true };
  } catch (error: any) {
    console.error('Failed to save to gallery:', error);
    console.error('Error details:', JSON.stringify(error, null, 2));
    return { success: false, error: error.message || 'Unknown error' };
  }
};

/**
 * Result type for batch gallery save operations
 */
export type BatchGallerySaveResult = {
  successCount: number;
  error?: string;
};

/**
 * Save multiple images to Android Gallery
 */
export const saveAllImagesToGallery = async (
  imagesUrlsOrBase64: string[],
  fileNamePrefix: string = 'design'
): Promise<BatchGallerySaveResult> => {
  if (!isNativePlatform() || getPlatform() !== 'android') {
    return { successCount: 0, error: 'Not Android' };
  }

  // Note: Permissions are handled by the Media plugin automatically on first save

  let successCount = 0;

  for (let i = 0; i < imagesUrlsOrBase64.length; i++) {
    try {
      const result = await saveImageToGallery(
        imagesUrlsOrBase64[i],
        `${fileNamePrefix}-${i + 1}.jpg`
      );
      if (result.success) {
        successCount++;
        console.log(`Image ${i + 1}/${imagesUrlsOrBase64.length} saved successfully`);
      } else {
        console.error(`Failed to save image ${i + 1}:`, result.error);
      }
    } catch (e) {
      console.error(`Failed to save image ${i + 1}:`, e);
    }

    // Small delay between saves to avoid overwhelming the system
    if (i < imagesUrlsOrBase64.length - 1) {
      await new Promise(resolve => setTimeout(resolve, 200));
    }
  }

  console.log(`Batch save complete: ${successCount}/${imagesUrlsOrBase64.length} images saved`);
  return { successCount };
};
